/**
 * Settings logic, with no React, no IndexedDB and no DOM in sight.
 *
 * Everything here is a pure function over plain data. That is deliberate: the
 * rules this file encodes are the ones that would quietly corrupt the record if
 * they were wrong — how many stops a rubric may have, what "archive" means as
 * opposed to "delete", which field of a conflict differs from the server's, and
 * whether a guest may become a person. A DOM harness is not needed to test any
 * of that, so none of it is allowed to depend on one.
 *
 * The screens in this directory are thin over these functions.
 */

import type {
  Aspect,
  AspectKind,
  DepthMode,
  Entity,
  Polarity,
  Profile,
  Stop,
  SubQuestion,
} from "../../../shared/types.ts";
import type { SidelinedOp } from "../../store/outbox.ts";

// ---------------------------------------------------------------------------
// Stop sets
// ---------------------------------------------------------------------------

/**
 * Five is the floor because fewer stops cannot carry a centre plus two grades
 * either side; seven is the ceiling because past that people stop reading the
 * labels and start estimating a position, which is the numeric slider this app
 * exists to avoid.
 */
export const MIN_STOPS = 5;
export const MAX_STOPS = 7;

export type StopIssueCode = "count" | "blank" | "duplicate" | "centre";

export interface StopIssue {
  code: StopIssueCode;
  message: string;
  /** Index into the stop list, when the issue belongs to one stop. */
  index?: number;
}

/**
 * A bipolar axis stores a signed value with the neutral reading at zero, so it
 * needs a middle stop to be neutral *at*. That restricts bipolar rubrics to an
 * odd number of stops — 5 or 7 — and the editor says so rather than letting
 * someone build an axis whose centre falls between two labels.
 */
export function validateStops(stops: readonly Stop[], polarity: Polarity): StopIssue[] {
  const issues: StopIssue[] = [];

  if (stops.length < MIN_STOPS || stops.length > MAX_STOPS) {
    issues.push({
      code: "count",
      message: `A rubric needs ${MIN_STOPS}–${MAX_STOPS} stops. This one has ${stops.length}.`,
    });
  }

  stops.forEach((stop, index) => {
    if (stop.label.trim() === "") {
      issues.push({
        code: "blank",
        index,
        message: `Stop ${index + 1} has no label. Every stop names a state.`,
      });
    }
  });

  const seenLabels = new Set<string>();
  const seenValues = new Set<number>();
  stops.forEach((stop, index) => {
    const key = stop.label.trim().toLowerCase();
    if (key !== "" && seenLabels.has(key)) {
      issues.push({
        code: "duplicate",
        index,
        message: `Two stops both say “${stop.label.trim()}”.`,
      });
    }
    seenLabels.add(key);
    if (seenValues.has(stop.value)) {
      issues.push({ code: "duplicate", index, message: `Two stops share the value ${stop.value}.` });
    }
    seenValues.add(stop.value);
  });

  if (polarity === "BIPOLAR" && stops.length > 0) {
    const values = stops.map((s) => s.value);
    const lowest = Math.min(...values);
    const highest = Math.max(...values);
    // Symmetry, not merely the presence of a zero: an even stop count numbers
    // as -3..2, which has a centre but puts one more grade on one side than the
    // other, so the same distance from neutral means different things per end.
    if (!values.includes(0) || lowest !== -highest) {
      issues.push({
        code: "centre",
        message:
          "A bipolar axis needs a neutral centre with the same number of stops either side. Use 5 or 7.",
      });
    }
  }

  return issues;
}

export function stopsValid(stops: readonly Stop[], polarity: Polarity): boolean {
  return validateStops(stops, polarity).length === 0;
}

/**
 * Assign values to a list of labels. Bipolar axes are signed around a zero
 * centre (the seeded convention); unipolar axes count from one. Values are
 * derived rather than edited because a hand-typed value set is one typo away
 * from an axis with two centres.
 */
export function renumberStops(labels: readonly string[], polarity: Polarity): Stop[] {
  if (polarity === "BIPOLAR") {
    const centre = Math.floor(labels.length / 2);
    return labels.map((label, i) => ({ value: i - centre, label }));
  }
  return labels.map((label, i) => ({ value: i + 1, label }));
}

export function labelsOf(stops: readonly Stop[]): string[] {
  return stops.map((s) => s.label);
}

export function addStopAt(labels: readonly string[], index: number): string[] {
  if (labels.length >= MAX_STOPS) return [...labels];
  const next = [...labels];
  next.splice(Math.min(Math.max(index + 1, 0), next.length), 0, "");
  return next;
}

export function removeStopAt(labels: readonly string[], index: number): string[] {
  if (labels.length <= MIN_STOPS) return [...labels];
  const next = [...labels];
  next.splice(index, 1);
  return next;
}

export function setLabelAt(labels: readonly string[], index: number, label: string): string[] {
  const next = [...labels];
  if (index >= 0 && index < next.length) next[index] = label;
  return next;
}

/** True when the two rubrics differ in a way a rater would notice. */
export function stopsChanged(before: readonly Stop[], after: readonly Stop[]): boolean {
  if (before.length !== after.length) return true;
  return before.some((stop, i) => {
    const other = after[i];
    return other === undefined || other.value !== stop.value || other.label !== stop.label;
  });
}

// ---------------------------------------------------------------------------
// Copy for the load-bearing choices
//
// `kind`, `polarity` and `stable` change what the app does with a number, not
// how it looks. One plain sentence each, kept next to the logic so the screens
// cannot drift into describing them differently.
// ---------------------------------------------------------------------------

export interface ChoiceCopy {
  title: string;
  line: string;
}

export const KIND_COPY: Record<AspectKind, ChoiceCopy> = {
  DESCRIPTIVE: {
    title: "Descriptive",
    line: "Says what the game was like. Never averaged — shown as the spread of what people said, headed by the label most of them picked.",
  },
  EVALUATIVE: {
    title: "Evaluative",
    line: "Says how good it was. May be averaged, and the average is always shown beside how many rated it and how far apart they were.",
  },
};

export const POLARITY_COPY: Record<Polarity, ChoiceCopy> = {
  BIPOLAR: {
    title: "Two-ended",
    line: "Runs between two opposites with a neutral middle, like luck against strategy. Neither end is the good end.",
  },
  UNIPOLAR: {
    title: "One-ended",
    line: "Runs from worst to best, like the rulebook. More is better.",
  },
};

export const STABLE_COPY: ChoiceCopy = {
  title: "Ask once per game",
  line: "The box does not change between plays. Asked the first time you play a game and not again, unless you change the answer yourself.",
};

export const STARRED_COPY: ChoiceCopy = {
  title: "In the default set",
  line: "Offered automatically when a session starts. Everything else stays one tap away.",
};

export const DEPTH_COPY: Record<DepthMode, ChoiceCopy> = {
  FULL: { title: "Full", line: "Every aspect the session is set up for." },
  SIMPLE: { title: "Simple", line: "The one score, plus the starred aspects. Nothing else is offered." },
  KID: { title: "Kid", line: "The one score and nothing else. Two taps and they are done." },
};

/**
 * The sentence that belongs anywhere someone edits a rubric.
 *
 * Sessions freeze their own copy of every aspect at the moment they start, so
 * editing here cannot reach backwards. People do not know that, and the fear
 * that they are about to corrupt three years of reviews is exactly what stops
 * them tidying a taxonomy that has stopped fitting them.
 */
export const HISTORY_SAFE_NOTE =
  "Editing this changes future sessions only. Every past session kept its own copy of the wording it was rated with.";

// ---------------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------------

export interface Sortable {
  id: string;
  sort: number;
  version: number;
}

/** Sorted for display: by `sort`, then by name so ties are at least stable. */
export function bySort<T extends { sort: number; name: string }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
}

export function nextSort(rows: readonly { sort: number }[]): number {
  return rows.reduce((max, row) => Math.max(max, row.sort), -1) + 1;
}

/**
 * Move one row up or down. Returns only the rows whose `sort` actually changed,
 * each carrying the version it was read at so the caller can send a correct
 * `base_version`. Sorts are renormalised to their index on the way through,
 * which quietly repairs duplicate or gappy values from an older client.
 */
export function reorder<T extends Sortable>(
  ordered: readonly T[],
  id: string,
  delta: -1 | 1,
): { id: string; sort: number; version: number }[] {
  const from = ordered.findIndex((row) => row.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ordered.length) return [];

  const moved = [...ordered];
  const [row] = moved.splice(from, 1);
  if (!row) return [];
  moved.splice(to, 0, row);

  const changes: { id: string; sort: number; version: number }[] = [];
  moved.forEach((item, index) => {
    if (item.sort !== index) changes.push({ id: item.id, sort: index, version: item.version });
  });
  return changes;
}

// ---------------------------------------------------------------------------
// Archive vs delete
// ---------------------------------------------------------------------------

export interface Archivable {
  archived_at: number | null;
}

export function isArchived(row: Archivable): boolean {
  return row.archived_at !== null;
}

export function archivePatch(now: number = Date.now()): { archived_at: number } {
  return { archived_at: now };
}

export function restorePatch(): { archived_at: null } {
  return { archived_at: null };
}

export interface Usage {
  /** How many sessions hold a frozen snapshot of this row. */
  sessions: number;
}

export interface Disposal {
  /** Always offered. An archived row leaves the pickers and stays in the record. */
  archive: { archived_at: number };
  /**
   * Permanent tombstone. Offered only for a row nothing in the record refers
   * to — a mistake made and unmade — never as a way to tidy up.
   */
  destroy: { allowed: boolean; blocked_because: string | null };
}

export function disposalOptions(usage: Usage, now: number = Date.now()): Disposal {
  const used = usage.sessions > 0;
  return {
    archive: archivePatch(now),
    destroy: {
      allowed: !used,
      blocked_because: used
        ? `Used in ${usage.sessions} session${usage.sessions === 1 ? "" : "s"}. Those sessions are part of the record — archive it instead.`
        : null,
    },
  };
}

// ---------------------------------------------------------------------------
// Aspect drafts
// ---------------------------------------------------------------------------

export interface AspectDraft {
  name: string;
  kind: AspectKind;
  polarity: Polarity;
  stable: 0 | 1;
  starred: 0 | 1;
  labels: string[];
  sort: number;
}

export function newAspectDraft(sort: number): AspectDraft {
  return {
    name: "",
    kind: "EVALUATIVE",
    polarity: "UNIPOLAR",
    stable: 0,
    starred: 0,
    labels: ["", "", "", "", ""],
    sort,
  };
}

export function draftFromAspect(aspect: Aspect): AspectDraft {
  return {
    name: aspect.name,
    kind: aspect.kind,
    polarity: aspect.polarity,
    stable: aspect.stable,
    starred: aspect.starred,
    labels: labelsOf(aspect.stops),
    sort: aspect.sort,
  };
}

export function draftStops(draft: AspectDraft): Stop[] {
  return renumberStops(draft.labels, draft.polarity);
}

/** Everything wrong with a draft, in the order a person would fix it. */
export function draftProblems(draft: AspectDraft): string[] {
  const problems: string[] = [];
  if (draft.name.trim() === "") problems.push("Give the aspect a name.");
  for (const issue of validateStops(draftStops(draft), draft.polarity)) problems.push(issue.message);
  return problems;
}

/**
 * The patch for an aspect write.
 *
 * `rubric_version` moves when the stops themselves changed, or when the kind
 * did. Renaming an aspect or starring it leaves ratings comparable; rewording
 * the ladder they were made against does not, and the aggregation layer
 * segregates trends by this number rather than silently averaging across a
 * redefinition.
 *
 * Flipping DESCRIPTIVE↔EVALUATIVE is at least as breaking as rewording the
 * ladder: it changes what the axis *means*. A descriptive Luck↔Strategy axis is
 * never averaged — turn it evaluative without a version bump and the game page
 * starts printing a mean of 0.0 over a −2 and a +2, a number nobody expressed
 * about a question nobody was asked. The bump segregates instead of destroying:
 * the old nights stay readable under their own version.
 */
export function aspectPatchFrom(
  draft: AspectDraft,
  previous?: Aspect,
): Record<string, unknown> {
  const stops = draftStops(draft);
  const rubricVersion =
    previous === undefined
      ? 1
      : stopsChanged(previous.stops, stops) || previous.kind !== draft.kind
        ? previous.rubric_version + 1
        : previous.rubric_version;

  return {
    name: draft.name.trim(),
    kind: draft.kind,
    polarity: draft.polarity,
    stable: draft.stable,
    starred: draft.starred,
    stops,
    sort: draft.sort,
    rubric_version: rubricVersion,
    archived_at: previous?.archived_at ?? null,
  };
}

// ---------------------------------------------------------------------------
// Sub-question drafts
// ---------------------------------------------------------------------------

export interface SubQuestionDraft {
  name: string;
  labels: string[];
  sort: number;
}

export function newSubQuestionDraft(aspect: Aspect, sort: number): SubQuestionDraft {
  // Inherits the parent's ladder: a sub-question that reads on a different
  // scale from the aspect above it is a small trap for whoever answers it.
  return { name: "", labels: labelsOf(aspect.stops), sort };
}

export function draftFromSubQuestion(sub: SubQuestion): SubQuestionDraft {
  return { name: sub.name, labels: labelsOf(sub.stops), sort: sub.sort };
}

export function subQuestionProblems(draft: SubQuestionDraft, polarity: Polarity): string[] {
  const problems: string[] = [];
  if (draft.name.trim() === "") problems.push("Give the sub-question a name.");
  for (const issue of validateStops(renumberStops(draft.labels, polarity), polarity)) {
    problems.push(issue.message);
  }
  return problems;
}

export function subQuestionPatchFrom(
  draft: SubQuestionDraft,
  aspect: Aspect,
  previous?: SubQuestion,
): Record<string, unknown> {
  return {
    aspect_id: aspect.id,
    name: draft.name.trim(),
    stops: renumberStops(draft.labels, aspect.polarity),
    sort: draft.sort,
    archived_at: previous?.archived_at ?? null,
  };
}

// ---------------------------------------------------------------------------
// Profiles
// ---------------------------------------------------------------------------

/**
 * Player colours are stored as literal hex, not as a theme token, because a
 * person's dot has to mean the same person in both themes and inside an
 * exported backup. They are picked to stay distinguishable under the common
 * colour deficiencies — no red/green pair carries meaning on its own, and every
 * dot is always accompanied by the name.
 */
export const PROFILE_COLOURS: readonly { name: string; hex: string }[] = [
  { name: "Spice", hex: "#C4661E" },
  { name: "Slate", hex: "#3F5A66" },
  { name: "Ochre", hex: "#A8792C" },
  { name: "Moss", hex: "#4A6B3F" },
  { name: "Plum", hex: "#6B3F63" },
  { name: "Teal", hex: "#256F6B" },
  { name: "Clay", hex: "#93331C" },
  { name: "Fog", hex: "#6F6659" },
];

export function defaultColour(existing: readonly Profile[]): string {
  const taken = new Set(existing.map((p) => p.colour.toUpperCase()));
  const free = PROFILE_COLOURS.find((c) => !taken.has(c.hex));
  const fallback = PROFILE_COLOURS[existing.length % PROFILE_COLOURS.length];
  return free?.hex ?? fallback?.hex ?? "#C4661E";
}

export interface ProfileDraft {
  name: string;
  colour: string;
  avatar: string | null;
  depth_mode: DepthMode;
}

export function newProfileDraft(existing: readonly Profile[]): ProfileDraft {
  return { name: "", colour: defaultColour(existing), avatar: null, depth_mode: "FULL" };
}

export function draftFromProfile(profile: Profile): ProfileDraft {
  return {
    name: profile.name,
    colour: profile.colour,
    avatar: profile.avatar,
    depth_mode: profile.depth_mode,
  };
}

export function profileProblems(draft: ProfileDraft): string[] {
  const problems: string[] = [];
  if (draft.name.trim() === "") problems.push("Give the player a name.");
  if (draft.colour.trim() === "") problems.push("Pick a colour.");
  return problems;
}

export function profilePatchFrom(draft: ProfileDraft): Record<string, unknown> {
  return {
    name: draft.name.trim(),
    colour: draft.colour,
    avatar: draft.avatar,
    depth_mode: draft.depth_mode,
    is_guest: 0,
    archived_at: null,
  };
}

// ---------------------------------------------------------------------------
// Guest promotion
// ---------------------------------------------------------------------------

export function isGuest(profile: Profile): boolean {
  return profile.is_guest === 1;
}

export function promotionDraft(profile: Profile, existing: readonly Profile[]): ProfileDraft {
  return {
    name: profile.name,
    // A guest chip made at the table may not have been given a colour anyone
    // chose; fall back to the next free one rather than to a duplicate.
    colour: profile.colour.trim() === "" ? defaultColour(existing) : profile.colour,
    avatar: profile.avatar,
    depth_mode: profile.depth_mode,
  };
}

/** Blocking reason, or null when the promotion may proceed. */
export function promotionProblem(profile: Profile, draft: ProfileDraft): string | null {
  if (!isGuest(profile)) return "This is already a saved player.";
  const problems = profileProblems(draft);
  return problems[0] ?? null;
}

/**
 * Non-blocking warning. A guest typed as "Lea" when a Lea already exists is
 * usually the same human, and the right fix is to merge — which this app does
 * not do yet. Saying so beats silently creating a second Lea.
 */
export function promotionWarning(draft: ProfileDraft, existing: readonly Profile[]): string | null {
  const name = draft.name.trim().toLowerCase();
  if (name === "") return null;
  const clash = existing.find(
    (p) => p.is_guest === 0 && p.deleted !== 1 && p.name.trim().toLowerCase() === name,
  );
  return clash
    ? `There is already a saved player called ${clash.name}. This will make a second one — their play histories stay separate.`
    : null;
}

/**
 * The patch that turns a guest chip into a person.
 *
 * The row keeps its id, so every session the guest already rated in still
 * points at it and those ratings become part of the new profile's history. That
 * is the whole point of promoting rather than re-creating.
 */
export function promoteGuestPatch(
  profile: Profile,
  draft: ProfileDraft,
): Record<string, unknown> {
  const problem = promotionProblem(profile, draft);
  if (problem) throw new Error(problem);
  return profilePatchFrom(draft);
}

// ---------------------------------------------------------------------------
// Conflicts
// ---------------------------------------------------------------------------

const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  colour: "Colour",
  avatar: "Avatar",
  depth_mode: "Depth",
  archived_at: "Archived",
  is_guest: "Guest",
  kind: "Kind",
  polarity: "Polarity",
  stable: "Ask once per game",
  starred: "In default set",
  stops: "Stops",
  sort: "Order",
  rubric_version: "Rubric version",
  value: "Rating",
  na: "Doesn't apply",
  note: "Note",
  verdict: "Verdict",
  status: "Status",
  played_at: "Played",
  title: "Title",
};

const ENTITY_LABELS: Partial<Record<Entity, string>> = {
  profile: "Player",
  game: "Game",
  aspect: "Aspect",
  subquestion: "Sub-question",
  play_session: "Session",
  session_participant: "Participant",
  session_aspect: "Session aspect",
  session_subquestion: "Session sub-question",
  headline_rating: "Score",
  aspect_rating: "Aspect rating",
  subquestion_rating: "Sub-question rating",
  play_again: "Would play again",
};

export function entityLabel(entity: Entity): string {
  return ENTITY_LABELS[entity] ?? entity;
}

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/** Human-readable rendering of a stored value, for the mine/theirs columns. */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (Array.isArray(value)) {
    const stops = value as Stop[];
    if (stops.every((s) => s && typeof s === "object" && "label" in s)) {
      return stops.map((s) => s.label).join(" · ");
    }
    return JSON.stringify(value);
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export interface FieldDiff {
  field: string;
  label: string;
  mine: unknown;
  theirs: unknown;
  changed: boolean;
}

export interface ConflictView {
  op_id: string;
  entity: Entity;
  entityLabel: string;
  id: string;
  /** Best available name for the row, so the choice is about a thing, not an id. */
  title: string;
  at: number;
  baseVersion: number;
  serverVersion: number | null;
  diffs: FieldDiff[];
}

/**
 * Turn a sidelined conflict into something a person can decide about.
 *
 * Only the fields the local edit actually touched are compared: a diff of the
 * whole row would bury the one word that differs under twelve identical ones.
 */
export function describeConflict(record: SidelinedOp): ConflictView {
  const server = record.server_row ?? {};
  const patch = record.op.patch;

  const diffs: FieldDiff[] = Object.keys(patch).map((field) => {
    const mine = patch[field];
    const theirs = server[field];
    return {
      field,
      label: fieldLabel(field),
      mine,
      theirs,
      changed: formatValue(mine) !== formatValue(theirs),
    };
  });
  diffs.sort((a, b) => Number(b.changed) - Number(a.changed) || a.label.localeCompare(b.label));

  const title =
    typeof server["name"] === "string" && server["name"] !== ""
      ? server["name"]
      : typeof patch["name"] === "string" && patch["name"] !== ""
        ? (patch["name"] as string)
        : typeof server["title"] === "string" && server["title"] !== ""
          ? (server["title"] as string)
          : `…${record.op.id.slice(-6)}`;

  return {
    op_id: record.op_id,
    entity: record.op.entity,
    entityLabel: entityLabel(record.op.entity),
    id: record.op.id,
    title,
    at: record.at,
    baseVersion: record.op.base_version,
    serverVersion: record.server_version ?? null,
    diffs,
  };
}

export interface DeadLetterView {
  op_id: string;
  entity: Entity;
  entityLabel: string;
  id: string;
  reason: string;
  at: number;
  fields: { label: string; value: string }[];
}

export function describeDeadLetter(record: SidelinedOp): DeadLetterView {
  return {
    op_id: record.op_id,
    entity: record.op.entity,
    entityLabel: entityLabel(record.op.entity),
    id: record.op.id,
    reason: record.reason ?? "The server refused this change without saying why.",
    at: record.at,
    fields: Object.keys(record.op.patch).map((field) => ({
      label: fieldLabel(field),
      value: formatValue(record.op.patch[field]),
    })),
  };
}

// ---------------------------------------------------------------------------
// Time and backup honesty
// ---------------------------------------------------------------------------

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function formatAgo(ms: number | null): string {
  if (ms === null) return "never";
  if (ms < MINUTE) return "just now";
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)} min ago`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)} h ago`;
  const days = Math.floor(ms / DAY);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

export type BackupTone = "never" | "stale" | "fresh";

export interface BackupStatus {
  tone: BackupTone;
  headline: string;
  detail: string;
}

/** A week without an export is long enough that saying nothing would be a lie. */
export const BACKUP_STALE_AFTER = 7 * DAY;

/**
 * What to say about the backup, said plainly.
 *
 * Browser storage is evictable even for an installed app —
 * `navigator.storage.persist()` is a hint, not a promise — so "never backed up"
 * is a real risk of losing everything, not a nag, and it is worded that way.
 */
export function backupStatus(agoMs: number | null, pending: number): BackupStatus {
  const unsynced =
    pending > 0
      ? ` ${pending} change${pending === 1 ? "" : "s"} on this device ${pending === 1 ? "has" : "have"} not reached the server yet.`
      : "";

  if (agoMs === null) {
    return {
      tone: "never",
      headline: "Never backed up",
      detail: `This device is the only copy of anything it has not synced. Browsers delete storage when space runs short, without asking.${unsynced}`,
    };
  }
  if (agoMs >= BACKUP_STALE_AFTER) {
    return {
      tone: "stale",
      headline: `Last backup ${formatAgo(agoMs)}`,
      detail: `Anything rated since then exists only here and on the server.${unsynced}`,
    };
  }
  return {
    tone: "fresh",
    headline: `Last backup ${formatAgo(agoMs)}`,
    detail: `Saved as a file you can restore from.${unsynced}`,
  };
}

/** Filename for an export. Dated so a folder of them sorts itself. */
export function backupFilename(now: number = Date.now()): string {
  const d = new Date(now);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `punchboard-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.json`;
}

/**
 * What a local wipe destroys, named specifically. A confirmation that says
 * "are you sure?" and nothing else is not a confirmation.
 */
export function wipeConsequences(counts: {
  sessions: number;
  profiles: number;
  aspects: number;
  pending: number;
  conflicts: number;
  deadLetters: number;
}): string[] {
  const lines = [
    `${counts.sessions} session${counts.sessions === 1 ? "" : "s"} held on this device`,
    `${counts.profiles} player profile${counts.profiles === 1 ? "" : "s"}`,
    `${counts.aspects} aspect${counts.aspects === 1 ? "" : "s"} of your taxonomy`,
  ];
  if (counts.pending > 0) {
    lines.push(
      `${counts.pending} change${counts.pending === 1 ? "" : "s"} that never reached the server — these are lost for good`,
    );
  }
  if (counts.conflicts > 0) lines.push(`${counts.conflicts} unresolved conflict(s)`);
  if (counts.deadLetters > 0) lines.push(`${counts.deadLetters} refused change(s)`);
  return lines;
}

/** The word the user has to type to wipe. Not "yes" — "yes" is muscle memory. */
export const WIPE_PHRASE = "ERASE";
